import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const RISK_LEVELS = ['green', 'yellow', 'red', 'insufficient_data'];
export const PERIODS = ['day_1_30', 'day_31_60', 'day_61_90', 'extension'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useObRiskWatchApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    // Default to latestOnly=true so the dashboard shows one row per onboarding.
    const baseParams = { latestOnly: 'true' };
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? { ...baseParams, ...queryParams }
      : (lastValidQueryParams.current || { ...baseParams, pageNumber: 1, pageSize: 10 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/onboarding-risk?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const recompute = useCallback(async (onboardingId) => {
    try { await api.post(`/v1/onboarding-risk/for-onboarding/${onboardingId}/recompute`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const levelLabel = (s) => t(`onboardingRisk.level.${s}`, { defaultValue: s });
    const periodLabel = (s) => t(`onboardingRisk.period.${s}`, { defaultValue: s });

    const renderComponent = (v) => v == null ? '–' : Math.round(Number(v)) + '%';

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'onboardingId', label: 'OB', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('onboardingRisk.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'currentPeriod',
        label: t('onboardingRisk.periodLabel'),
        options: {
          ...multiSelectFilter(t('onboardingRisk.periodLabel'), PERIODS, periodLabel),
          customBodyRender: (v) => periodLabel(v),
        },
      },
      {
        name: 'riskLevel',
        label: t('onboardingRisk.levelLabel'),
        options: {
          ...multiSelectFilter(t('onboardingRisk.levelLabel'), RISK_LEVELS, levelLabel),
          customBodyRender: (v) => levelLabel(v),
        },
      },
      (() => {
        const col = numericFilterColumn('compositeScore', t('onboardingRisk.composite'), { decimals: 0 });
        return {
          ...col,
          options: {
            ...col.options,
            // Suppress the misleading flat score when there is no signal yet —
            // the riskLevel column already reads "Veri Bekleniyor" in that case.
            customBodyRender: (v, tableMeta) => {
              const row = data[tableMeta.rowIndex];
              if (row?.riskLevel === 'insufficient_data') return '–';
              return v == null ? '–' : Math.round(Number(v));
            },
          },
        };
      })(),
      {
        name: 'npsComponent',
        label: t('onboardingRisk.nps'),
        options: { filter: false, sort: true, customBodyRender: renderComponent },
      },
      {
        name: 'taskCompletionComponent',
        label: t('onboardingRisk.task'),
        options: { filter: false, sort: true, customBodyRender: renderComponent },
      },
      {
        name: 'oneOnOneComponent',
        label: t('onboardingRisk.oneOnOne'),
        options: { filter: false, sort: true, customBodyRender: renderComponent },
      },
      {
        name: 'trainingPaceComponent',
        label: t('onboardingRisk.training'),
        options: { filter: false, sort: true, customBodyRender: renderComponent },
      },
      {
        name: 'portalActivityComponent',
        label: t('onboardingRisk.portal'),
        options: { filter: false, sort: true, customBodyRender: renderComponent },
      },
      dateFilterColumn('snapshotDate', t('onboardingRisk.snapshotDate'), lang),
    ];
  }, [t, lang, data]);

  return { fetching, data, pagination, columns, refresh, recompute };
};

export default useObRiskWatchApi;
